import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, Users, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { downloadPdf } from '../../utils/pdfUtils'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { const d = s.split('T')[0]; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}` } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 24px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 14 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 12px' },
  hdrT1: { fontSize: 18, fontWeight: 700 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 },
  th: { border: '1px solid #1f2937', padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: 11, background: '#f3f4f6' },
  td: { border: '1px solid #d1d5db', padding: '5px 8px', verticalAlign: 'middle', fontSize: 12 },
  tdC: { border: '1px solid #d1d5db', padding: '5px 8px', textAlign: 'center', verticalAlign: 'middle', fontSize: 12 },
  tdR: { border: '1px solid #d1d5db', padding: '5px 8px', textAlign: 'right', verticalAlign: 'middle', fontSize: 12 },
  footer: { textAlign: 'left', fontSize: 10, color: '#6b7280', marginTop: 16, paddingTop: 8, borderTop: '1px solid #e5e7eb' },
}

export default function ListagemCompradores() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [vendedorPor, setVendedorPor] = useState('fazenda')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setDados(null); return }
    setLoading(true)
    mapasService.getListagemCompradoresDados(orgId, leilaoId, vendedorPor)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId, vendedorPor])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getListagemCompradoresPdf(orgId, leilaoId, vendedorPor)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  const totalQtd = dados?.lotes?.reduce((s, l) => s + l.qtd, 0) || 0
  const totalValor = dados?.lotes?.reduce((s, l) => s + l.vlr_total, 0) || 0

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-semibold text-gray-800">Listagem de Compradores</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => { setLeilaoId(v); setDados(null) }} disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <Select label="Vendedor exibido por" value={vendedorPor} onChange={setVendedorPor} className="min-w-[180px]">
            <option value="fazenda">Fazenda</option>
            <option value="nome">Nome do vendedor</option>
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Selecione um leilão</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <div style={S.page}>
            <div style={S.hdr}>
              <div style={{ minWidth: 120 }}>{logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}</div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>{leilaoAtual?.nome} — {fmtDate(leilaoAtual?.data_leilao)}</div>
              </div>
              <div style={{ minWidth: 120, textAlign: 'right' }}><img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} /></div>
            </div>

            <table style={S.tbl}>
              <thead><tr>
                <th style={S.th}>LT</th>
                <th style={S.th}>QTDE</th>
                <th style={S.th}>SEXO</th>
                <th style={S.th}>IDADE</th>
                <th style={S.th}>VLR UNIT</th>
                <th style={S.th}>{dados.col_valor_label || 'VLR TOTAL'}</th>
                <th style={S.th}>VCTO</th>
                <th style={S.th}>VENDEDOR</th>
                <th style={S.th}>COMPRADOR</th>
              </tr></thead>
              <tbody>
                {dados.lotes.map((l, i) => (
                  <tr key={i}>
                    <td style={S.tdC}><strong>{l.numero_lote}</strong></td>
                    <td style={S.tdC}>{l.qtd}</td>
                    <td style={S.tdC}>{l.sexo === 'Macho' ? 'M' : l.sexo === 'Fêmea' || l.sexo === 'Femea' ? 'F' : l.sexo || '—'}</td>
                    <td style={S.tdC}>{l.idade || '—'}</td>
                    <td style={S.tdR}>{brl(l.vlr_unit)}</td>
                    <td style={S.tdR}>{brl(l.vlr_total)}</td>
                    <td style={S.tdC}>{fmtDate(l.vcto) || '—'}</td>
                    <td style={S.td}>{l.vendedor}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{l.comprador}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ fontSize: 11, fontWeight: 700, textAlign: 'right', marginTop: 6 }}>
              Lotes: {dados.lotes.length} | Animais: {totalQtd} | Total: {brl(totalValor)}
            </div>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Listagem de Compradores — {leilaoAtual?.nome}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, `Listagem Compradores - ${leilaoAtual?.nome || ''}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">Salvar PDF</button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title="Listagem Compradores" style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}
    </div>
  )
}
